import { Dispatch, SetStateAction } from 'react';

/**
 * Starts a shimmer buildup effect by incrementally adding shimmer start positions to an array at random intervals.
 *
 * @param {Object} params - The parameters for shimmer buildup.
 * @param {Dispatch<SetStateAction<string[]>>} params.setShimmerStarts
 *   State setter for shimmer start positions.
 * @param {number} params.shimmerStep - The increment (in px) for each new shimmer line.
 * @param {number} params.maxShimmerStartPoint - The maximum shimmer start value before wrapping to 0.
 * @param {number} [params.initialInterval=800] - The initial delay before the first shimmer (ms).
 * @param {number} [params.minInterval=500] - The minimum random interval between shimmer lines (ms).
 * @param {number} [params.maxInterval=1200] - The maximum random interval between shimmer lines (ms).
 * @param {(timeoutId: NodeJS.Timeout | null) => void} [params.onCleanup]
 *   Optional callback to receive the timeoutId for cleanup.
 * @returns {() => void} Cleanup function to clear the shimmer buildup timer.
 */
type ShimmerBuildupParams = {
  setShimmerStarts: Dispatch<SetStateAction<string[]>>;
  shimmerStep: number;
  maxShimmerStartPoint: number;
  initialInterval?: number;
  minInterval?: number;
  maxInterval?: number;
  onCleanup?: (timeoutId: NodeJS.Timeout | null) => void;
};

export function startShimmerBuildup({
  setShimmerStarts,
  shimmerStep,
  maxShimmerStartPoint,
  initialInterval = 800,
  minInterval = 500,
  maxInterval = 1200,
  onCleanup,
}: ShimmerBuildupParams) {
  let timeoutId: NodeJS.Timeout | null = null;
  const addShimmer = () => {
    setShimmerStarts(prev => {
      const last = parseInt(prev[prev.length - 1], 10);
      let next = last + shimmerStep;
      if (next > maxShimmerStartPoint) next = 0;
      if (next === 0 && prev.includes('0px')) return prev;
      return [...prev, `${next}px`];
    });
    const nextInterval = minInterval + Math.floor(Math.random() * (maxInterval - minInterval + 1));
    timeoutId = setTimeout(addShimmer, nextInterval);
    if (onCleanup) onCleanup(timeoutId);
  };
  timeoutId = setTimeout(addShimmer, initialInterval);
  if (onCleanup) onCleanup(timeoutId);
  return () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
}
